import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { GetDatesRequestInfo, GoalsSummaryData } from '@app/client/pg-goals/pg-goals.utilities';
import { environment } from '@env/environment';
import { select } from '@ngxs/store';
import { Observable, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { CampaignOptimizationValue } from '../models/common.models';
import {
  COST_PER_CLICK,
  COST_PER_CONVERSION,
  COST_PER_CONVERSION_CLICKS,
  COST_REVENUE_RATIO,
  COST_REVENUE_RATIO_CLICKS,
  PG_GOAL_METRIC_COST_PER_CLICK,
  PG_GOAL_METRIC_COST_PER_CONVERSION,
  PG_GOAL_METRIC_COST_PER_CONVERSION_CLICKS,
  PG_GOAL_METRIC_COST_PER_DAY,
  PG_GOAL_METRIC_CRR,
  PG_GOAL_METRIC_CRR_CLICKS,
  PG_GOAL_METRIC_PROFIT_MAXIMIZATION,
  PG_GOAL_METRIC_ROAS,
  PG_GOAL_METRIC_ROAS_CLICKS,
  PG_GOAL_METRIC_ROI,
  PROFIT_NET,
  RETURN_ON_INVESTMENT,
  ROAS,
  ROAS_CLICKS,
} from '../models/metrics.constants';
import { LoggingService } from '../services/error-logging.service';
import { convertMicrostoNumbers, getCostFieldName } from '../services/metrics-calculations.service';
import { CustomerState } from '../store/states/customer/customer.state';

const state = { NO_DATA: '0', ERROR: '-1' };
export function hasHistoricPerfData(summary = {}) {
  if (summary == null) return false;
  return Object.values(summary).some((d: any) => ![0, '—', '-', null].includes(d));
}

@Injectable({
  providedIn: 'root',
})
export class PGAPIService {
  readonly customerId = select(CustomerState.customerId);

  private http = inject(HttpClient);
  private logger = inject(LoggingService);

  hasPG(name, pgId = null): Observable<boolean> {
    return this.http
      .get(
        `${environment.app.resources.dashboard}customers/${this.customerId()}/performance-groups?search_name=${encodeURIComponent(name)}&limit=1000&include=name,performance_group_id`,
        {},
      )
      .pipe(
        map((response: any) => {
          const matchingPGs = response.data.filter(
            (d) => d.performance_group_id != pgId && d.name.toLowerCase() === name.toLowerCase(),
          );

          return matchingPGs.length > 0;
        }),
        catchError((error: any) => {
          this.logger.logError('Failed to fetch pg name', error, 'error');
          return throwError('Failed to fetch pg name');
        }),
      );
  }

  getCurrentGoalMetrics(
    currency,
    optiType: CampaignOptimizationValue,
    campaigns = [],
    performanceGroupIds = null,
    dates = null,
  ) {
    const payload: Record<string, any> = { ...GetDatesRequestInfo(dates), currency };

    if (campaigns.length) payload['filter_adspert_campaign_id'] = campaigns;
    else if (!!performanceGroupIds) payload['filter_performance_group_id'] = performanceGroupIds;

    return this.http
      .post(`${environment.app.resources.dashboard}customers/${this.customerId()}/performance`, payload, {})
      .pipe(
        map((res: any) => {
          return {
            data: convertMicrostoNumbers(res.data).map((b) => ({
              [PG_GOAL_METRIC_COST_PER_DAY]: b[getCostFieldName(optiType)],
              [PG_GOAL_METRIC_COST_PER_CLICK]: b[COST_PER_CLICK],
              [PG_GOAL_METRIC_COST_PER_CONVERSION]: b[COST_PER_CONVERSION],
              [PG_GOAL_METRIC_CRR]: b[COST_REVENUE_RATIO],
              [PG_GOAL_METRIC_ROI]: b[RETURN_ON_INVESTMENT],
              [PG_GOAL_METRIC_ROAS]: b[ROAS],
              [PG_GOAL_METRIC_PROFIT_MAXIMIZATION]: b[PROFIT_NET],
              [PG_GOAL_METRIC_ROAS_CLICKS]: b[ROAS_CLICKS],
              [PG_GOAL_METRIC_CRR_CLICKS]: b[COST_REVENUE_RATIO_CLICKS],
              [PG_GOAL_METRIC_COST_PER_CONVERSION_CLICKS]: b[COST_PER_CONVERSION_CLICKS],

              date: b.date,
            })),
            summary: GoalsSummaryData(res.data, PROFIT_NET, payload.limit),
          };
        }),
        catchError((error: any) => {
          this.logger.logError('Failed to fetch pg metrics', error, 'error');
          return throwError('Failed to fetch pg metrics');
        }),
      );
  }

  updatePG(payload): Observable<boolean> {
    return this.http
      .patch(`${environment.app.resources.dashboard}customers/${this.customerId()}/performance-groups`, [payload], {})
      .pipe(
        map(() => true),
        catchError((error: any) => {
          this.logger.logError('Failed to update pg ', error, 'error');
          return throwError(error?.original?.error);
        }),
      );
  }
}
